import type { UseQueryResult } from '@tanstack/react-query'
import { motion, useReducedMotion } from 'framer-motion'
import { RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import * as m from '~/paraglide/messages'
import { DURATION, EASING } from '~/shared/lib/motion'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'

const SKELETON_ROWS = ['a', 'b', 'c', 'd']

export function StatCard({
  title,
  hint,
  aside,
  className,
  children,
}: {
  title: string
  hint?: string
  aside?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <Card className={cn('flex min-w-0 flex-col gap-5 p-5 sm:p-6', className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <h3 className="font-medium text-foreground text-sm">{title}</h3>
          {hint && <p className="text-muted-foreground text-xs leading-relaxed">{hint}</p>}
        </div>
        {aside}
      </div>
      {children}
    </Card>
  )
}

function GrowingBar({ share, color }: { share: number; color: string }) {
  const reduced = useReducedMotion() ?? false
  return (
    <motion.span
      className="absolute inset-y-0 left-0 origin-left rounded-full"
      style={{ width: `${Math.min(100, Math.max(0, share * 100))}%`, backgroundColor: color }}
      initial={reduced ? false : { scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: (DURATION.slow * 2) / 1000, ease: EASING }}
    />
  )
}

export function MeterRow({
  label,
  value,
  share,
  shareText,
  color,
}: {
  label: string
  value: string
  share: number | null
  shareText?: string
  color: string
}) {
  return (
    <li className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate text-muted-foreground">{label}</span>
        <span className="flex shrink-0 items-baseline gap-2 tabular-nums">
          <span className="font-medium text-foreground">{value}</span>
          {shareText && (
            <span className="w-10 text-right text-muted-foreground text-xs">{shareText}</span>
          )}
        </span>
      </div>
      <span className="relative block h-1 overflow-hidden rounded-full bg-foreground/6">
        {share !== null && share > 0 && <GrowingBar share={share} color={color} />}
      </span>
    </li>
  )
}

export function MeterList({ children }: { children: ReactNode }) {
  return <ul className="space-y-3.5">{children}</ul>
}

interface SplitPart {
  key: string
  label: string
  value: number
  valueText: string
  color: string
}

export function SplitBar({ parts, label }: { parts: readonly SplitPart[]; label: string }) {
  const total = parts.reduce((sum, part) => sum + part.value, 0)
  return (
    <div className="space-y-3">
      <div
        role="img"
        aria-label={label}
        className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-foreground/6"
      >
        {total > 0 &&
          parts
            .filter((part) => part.value > 0)
            .map((part) => (
              <span
                key={part.key}
                className="h-full first:rounded-l-full last:rounded-r-full"
                style={{ flexGrow: part.value, backgroundColor: part.color }}
              />
            ))}
      </div>
      <ul className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
        {parts.map((part) => (
          <li key={part.key} className="inline-flex items-center gap-1.5">
            <i className="size-2 rounded-[2px]" style={{ backgroundColor: part.color }} />
            <span className="text-muted-foreground">{part.label}</span>
            <span className="font-medium text-foreground tabular-nums">{part.valueText}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function FigureGrid({ className, children }: { className?: string; children: ReactNode }) {
  return <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-5', className)}>{children}</dl>
}

export function Figure({
  label,
  value,
  detail,
}: {
  label: string
  value: string
  detail?: string
}) {
  return (
    <div className="min-w-0 space-y-1">
      <dt className="truncate text-muted-foreground text-xs">{label}</dt>
      <dd className="font-semibold font-serif text-foreground text-xl tabular-nums leading-none tracking-tight">
        {value}
      </dd>
      {detail && <dd className="text-muted-foreground text-xs tabular-nums">{detail}</dd>}
    </div>
  )
}

export function StatsError({ query }: { query: Pick<UseQueryResult, 'refetch' | 'isFetching'> }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg bg-destructive/5 p-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-destructive text-sm">{m.statsLoadError()}</p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => query.refetch()}
        disabled={query.isFetching}
      >
        <RefreshCw className={cn(query.isFetching && 'motion-safe:animate-spin')} />
        {m.tryAgain()}
      </Button>
    </div>
  )
}

function RowsSkeleton() {
  return (
    <div className="space-y-4" aria-hidden>
      {SKELETON_ROWS.map((row) => (
        <div key={row} className="space-y-2">
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-1 w-full rounded-full" />
        </div>
      ))}
    </div>
  )
}

export function StatsQueryState<T>({
  query,
  children,
}: {
  query: UseQueryResult<T>
  children: (data: T) => ReactNode
}) {
  if (query.data !== undefined) return children(query.data)
  if (query.isError) return <StatsError query={query} />
  return <RowsSkeleton />
}
